import { useEffect, useMemo, useState } from "react";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { Eye } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import {
  usePortalPersonalImpersonation,
  type PersonalUser,
} from "@/contexts/PortalPersonalImpersonationContext";
import {
  fetchPersonalConCuenta,
  fetchUsuariosSistema,
  type PersonalConCuenta,
} from "@/lib/portal-personal/personal-usuarios";
import { SelectorVerComo, coincideVerComo, type GrupoVerComo } from "@/components/ui/selector-ver-como";

/** Con ~1.9k usuarios activos no se traen todos: se pide una página por búsqueda. */
const LIMITE = 50;
const DEBOUNCE_MS = 300;

const aPersonalUser = (p: PersonalConCuenta): PersonalUser => ({
  id: p.id,
  nombre: p.nombre,
  email: p.email,
  rol_nombre: p.rolNombre,
  rol_id: p.rolId,
  id_persona: p.personaId,
  auth_user_id: p.authUserId,
  tipo_personal: p.tipoPersonal,
});

/**
 * Selector "Ver como" del Portal del Personal.
 *
 * En los dos casos se listan SOLO usuarios que hoy entran al portal (su rol tiene
 * permisos en él y, al estar individualizado, están en su lista). Ver como alguien
 * que no puede abrirlo no enseña nada: la vista sale vacía y parece un error.
 *
 *   - Super Administrador: busca entre todos los que tienen acceso. La búsqueda es
 *     contra el servidor (son demasiados para filtrar en el cliente).
 *   - Otros roles con `puede_impersonar`: sólo personal de la organización con
 *     cuenta del sistema ligada, filtrado en el cliente como hasta ahora.
 *
 * Seleccionar a alguien sólo cambia de quién se habla; para ver el portal CON
 * SUS MENÚS hay que activar "Vista del usuario" en el conmutador `modo` del selector.
 *
 * Pintado con el estándar `SelectorVerComo` (búsqueda controlada, sin filtro de cmdk).
 */
export function PortalPersonalImpersonationSelector() {
  const { profile } = useAuth();
  const { impersonatedUser, setImpersonatedUser, clearImpersonation, isImpersonating } =
    usePortalPersonalImpersonation();
  const [open, setOpen] = useState(false);
  const [busqueda, setBusqueda] = useState("");
  const [terminoDiferido, setTerminoDiferido] = useState("");

  const canImpersonate = profile?.puede_impersonar === true;
  const esSuperAdmin = profile?.rol_nombre === "Super Administrador";

  // Cada tecla no dispara una consulta: se espera a que el usuario pare de escribir.
  useEffect(() => {
    const t = setTimeout(() => setTerminoDiferido(busqueda), DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [busqueda]);

  const { data: todos = [], isFetching: cargandoTodos } = useQuery({
    queryKey: ["portal-personal-usuarios-sistema", terminoDiferido],
    queryFn: () => fetchUsuariosSistema(terminoDiferido, LIMITE),
    enabled: canImpersonate && esSuperAdmin && open,
    // Sin esto la lista parpadea a vacía en cada tecleo.
    placeholderData: keepPreviousData,
    staleTime: 60_000,
  });

  const { data: personal = [], isFetching: cargandoPersonal } = useQuery({
    queryKey: ["portal-personal-suplantables"],
    queryFn: fetchPersonalConCuenta,
    enabled: canImpersonate && !esSuperAdmin,
    staleTime: 5 * 60_000,
  });

  const users: PersonalUser[] = useMemo(() => {
    if (esSuperAdmin) return todos.map(aPersonalUser);
    return personal
      .filter((p) => coincideVerComo(`${p.nombre} ${p.email}`, busqueda) > 0)
      .map(aPersonalUser);
  }, [esSuperAdmin, todos, personal, busqueda]);

  const isLoading = esSuperAdmin ? cargandoTodos : cargandoPersonal;
  const etiquetaDefecto = esSuperAdmin ? "Vista por defecto (Super Admin)" : "Vista por defecto";
  const vacio = esSuperAdmin
    ? "Ningún usuario con acceso al portal coincide con la búsqueda."
    : "Sin personal con cuenta ligada y acceso al portal.";

  if (!canImpersonate) return null;

  const etiqueta = isImpersonating
    ? impersonatedUser?.nombre || impersonatedUser?.email || "Usuario"
    : etiquetaDefecto;

  const grupos: GrupoVerComo[] = [
    {
      titulo: esSuperAdmin ? "Usuarios con acceso" : "Personal",
      opciones: users.map((u) => ({
        key: u.id,
        label: u.nombre,
        buscarPor: u.email,
        nota: u.tipo_personal ? `${u.rol_nombre} · ${u.tipo_personal}` : u.rol_nombre,
        seleccionada: impersonatedUser?.id === u.id,
        onSelect: () => setImpersonatedUser(u),
      })),
    },
  ];

  return (
    <SelectorVerComo
      etiqueta={etiqueta}
      impersonando={isImpersonating}
      opcionPorDefecto={{
        key: "__default__",
        label: etiquetaDefecto,
        nota: "Por defecto",
        seleccionada: !isImpersonating,
        onSelect: clearImpersonation,
        icono: <Eye className="mr-2 size-3.5 shrink-0 text-primary" />,
      }}
      grupos={grupos}
      onLimpiar={clearImpersonation}
      textoVacio={vacio}
      cargando={isLoading}
      // Búsqueda controlada: para Super Admin filtra el servidor (con debounce) y para los
      // demás el `useMemo` de arriba; en ambos casos cmdk no vuelve a filtrar.
      busqueda={busqueda}
      onBusquedaChange={setBusqueda}
      // La consulta de Super Admin solo corre con el popover abierto.
      onOpenChange={setOpen}
      pie={
        esSuperAdmin && users.length >= LIMITE
          ? `Mostrando los primeros ${LIMITE}. Afina la búsqueda para ver más.`
          : undefined
      }
    />
  );
}
